/**
 * User Service
 * Handles all user-related database operations using Supabase.
 */

import { SupabaseService } from '../supabase/supabaseService';
import { TABLES } from '../supabase/tables';

export class UserService extends SupabaseService {
  /**
   * Get all users.
   * @param {Object} options - Query options
   */
  static async getUsers(options = {}) {
    return this.getAll(TABLES.USERS, options);
  }

  /**
   * Get active (non-deactivated) users.
   */
  static async getActiveUsers() {
    return this.getWhere(TABLES.USERS, [
      { field: 'is_deactivated', operator: '!=', value: true }
    ]);
  }

  /**
   * Get a user by primary key.
   * @param {string} userId
   */
  static async getUserById(userId) {
    return this.getById(TABLES.USERS, userId);
  }

  /**
   * Get a user by email address.
   * @param {string} email
   */
  static async getUserByEmail(email) {
    const users = await this.getWhere(TABLES.USERS, [
      { field: 'email', operator: '==', value: email }
    ]);
    return users.length > 0 ? users[0] : null;
  }

  /**
   * Get users filtered by type (admin | caretaker | client).
   * @param {string} type
   */
  static async getUsersByType(type) {
    return this.getWhere(TABLES.USERS, [
      { field: 'type', operator: '==', value: type }
    ]);
  }

  /**
   * Create a new user record.
   * @param {Object} userData - { name, email, type, ... }
   * @returns {Promise<string>} New user id
   */
  static async createUser(userData) {
    return this.create(TABLES.USERS, {
      is_deactivated: false,
      is_admin: false,
      ...userData
    });
  }

  /**
   * Update user data.
   * @param {string} userId
   * @param {Object} updates
   */
  static async updateUser(userId, updates) {
    return this.update(TABLES.USERS, userId, updates);
  }

  /**
   * Soft-deactivate a user.
   * @param {string} userId
   * @param {number} deactivationTime - epoch ms
   */
  static async deactivateUser(userId, deactivationTime = Date.now()) {
    return this.updateUser(userId, {
      is_deactivated: true,
      deactivation_time: deactivationTime
    });
  }

  /**
   * Reactivate a user.
   * @param {string} userId
   */
  static async reactivateUser(userId) {
    return this.updateUser(userId, {
      is_deactivated: false,
      deactivation_time: null
    });
  }

  /**
   * Hard-delete a user.
   * @param {string} userId
   */
  static async deleteUser(userId) {
    return this.delete(TABLES.USERS, userId);
  }

  /**
   * Check if a user is admin.
   * @param {string} userId
   */
  static async isUserAdmin(userId) {
    const user = await this.getUserById(userId);
    return user ? !!user.is_admin : false;
  }

  /**
   * Get all active admin users.
   */
  static async getAdminUsers() {
    return this.getWhere(TABLES.USERS, [
      { field: 'is_admin', operator: '==', value: true },
      { field: 'is_deactivated', operator: '!=', value: true }
    ]);
  }

  /**
   * Update a user's admin status.
   * @param {string} userId
   * @param {boolean} isAdmin
   */
  static async updateAdminStatus(userId, isAdmin) {
    return this.updateUser(userId, { is_admin: isAdmin });
  }
}
